import type { components } from '#/api/schema'
import { createEnumGuard } from '#/utils/typeGuards'

export const ETICKET_MEDIA_TYPES = ['pdf', 'pkpass_apple', 'pkpass_google', 'graphic'] as const

export type TETicketMediaType = (typeof ETICKET_MEDIA_TYPES)[number]

export const isETicketMediaType = createEnumGuard(ETICKET_MEDIA_TYPES)

export interface IETicketMedia {
  id: string
  legIndex: number
  ticketName: string | null
  mode: string | null
  type: TETicketMediaType
  url: string
}

type TSchemaMedia = components['schemas']['ETicketMediaItem']

/** Normalise a schema media item so optional nulls become explicit `null`. */
export const mapETicketMedia = (item: TSchemaMedia): IETicketMedia => ({
  id: item.id,
  legIndex: item.legIndex,
  ticketName: item.ticketName ?? null,
  mode: item.mode ?? null,
  type: item.type,
  url: item.url,
})

export const mapETicketMediaList = (media: readonly TSchemaMedia[] | undefined): IETicketMedia[] =>
  (media ?? []).map(mapETicketMedia)

export const findGraphicMedia = (media: readonly IETicketMedia[]): IETicketMedia | undefined =>
  media.find((item) => item.type === 'graphic')

export const getGraphicUrl = (media: readonly IETicketMedia[]): string | undefined => findGraphicMedia(media)?.url

/** Tranzer repeats the same artefact on every leg it covers — keep one copy per (type, url). */
export const dedupeETicketMedia = (items: readonly IETicketMedia[]): IETicketMedia[] => {
  const seen = new Set<string>()
  const result: IETicketMedia[] = []

  for (const item of items) {
    const key = `${item.type}|${item.url}`
    if (seen.has(key)) continue
    seen.add(key)
    result.push(item)
  }

  return result
}

export const isDownloadableMedia = (item: IETicketMedia): boolean => item.type === 'pdf' || item.type === 'graphic'
